import type { Recording, RescueLevel, RescueRole, SimulationFrame, Vec, Rect } from './types';
import { motionRect, pulseActive } from './geometry';
export const ROLE_COLORS = { lead: '#3A82B4', follow: '#D07A43' };
const ROLE_SOFT = { lead: '#E4F1FA', follow: '#FBEEE2' };
/** Older scene snapshots carry several stacked zone rectangles; drawing them merged keeps the board calm. */
export function mergeZones(rects: Rect[]): Rect[] {
  const merged: Rect[] = [];
  for (const rect of rects) {
    let box = { ...rect };
    for (let index = 0; index < merged.length; ) {
      const other = merged[index];
      const touches = box.x <= other.x + other.w + 10 && other.x <= box.x + box.w + 10 && box.y <= other.y + other.h + 10 && other.y <= box.y + box.h + 10;
      if (!touches) { index++; continue; }
      const x = Math.min(box.x, other.x); const y = Math.min(box.y, other.y);
      box = { x, y, w: Math.max(box.x + box.w, other.x + other.w) - x, h: Math.max(box.y + box.h, other.y + other.h) - y };
      merged.splice(index, 1);
    }
    merged.push(box);
  }
  return merged;
}
const THEMES = {
  garden: { sky: '#EAF3EE', floor: '#D6E6DE', accent: '#4E8F6E', stone: '#6E8B86', cap: '#AEC8C0', wood: '#D9B681' },
  workshop: { sky: '#F3EDE1', floor: '#E2D8C8', accent: '#9A7748', stone: '#6F7A84', cap: '#B4BCC0', wood: '#DCC094' },
  sky: { sky: '#E7F1F8', floor: '#D2E2EC', accent: '#4C7E9C', stone: '#6C8CA3', cap: '#A8C7DA', wood: '#D8BE93' },
  night: { sky: '#E6EAF6', floor: '#D0D9EA', accent: '#5E6C92', stone: '#64708C', cap: '#AEB7D2', wood: '#D2BC98' },
};
const shade = (color: string) => {
  const value = color.replace('#', '');
  if (value.length !== 6) return '#C08552';
  const channels = [0, 2, 4].map(index => Math.max(0, Math.round(parseInt(value.slice(index, index + 2), 16) * 0.78)));
  return '#' + channels.map(channel => channel.toString(16).padStart(2, '0')).join('');
};

/** One calm paper-diorama board. Only things that carry meaning are saturated; scenery and work zones stay quiet. */
export function renderRescue(ctx: CanvasRenderingContext2D, level: RescueLevel, frame: SimulationFrame, x: number, y: number, size: number, future?: { lead: Recording; follow: Recording }, preview?: { points: Vec[]; color: string; spring: boolean }, activeRole?: RescueRole) {
  const theme = THEMES[level.theme ?? 'garden'];
  const time = frame.tick / 60;
  const path = (points: Vec[]) => { ctx.beginPath(); points.forEach((point, index) => index ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y)); };
  const line = (points: Vec[], color: string, width: number, dash: number[] = []) => {
    if (points.length < 2) return;
    path(points); ctx.strokeStyle = color; ctx.lineWidth = width; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.setLineDash(dash); ctx.stroke(); ctx.setLineDash([]);
  };
  const round = (rect: Rect, radius = 12) => {
    const k = Math.max(0, Math.min(radius, rect.w / 2, rect.h / 2));
    ctx.beginPath();
    ctx.moveTo(rect.x + k, rect.y);
    ctx.arcTo(rect.x + rect.w, rect.y, rect.x + rect.w, rect.y + rect.h, k);
    ctx.arcTo(rect.x + rect.w, rect.y + rect.h, rect.x, rect.y + rect.h, k);
    ctx.arcTo(rect.x, rect.y + rect.h, rect.x, rect.y, k);
    ctx.arcTo(rect.x, rect.y, rect.x + rect.w, rect.y, k);
    ctx.closePath();
  };
  const fill = (rect: Rect, color: string, radius = 12) => { round(rect, radius); ctx.fillStyle = color; ctx.fill(); };
  const panel = (rect: Rect, color: string, stroke: string | undefined, width: number, radius = 12) => { fill(rect, color, radius); if (stroke) { round(rect, radius); ctx.strokeStyle = stroke; ctx.lineWidth = width; ctx.stroke(); } };
  const circle = (px: number, py: number, radius: number, color: string, stroke?: string, width = 2) => { ctx.beginPath(); ctx.arc(px, py, radius, 0, Math.PI * 2); ctx.fillStyle = color; ctx.fill(); if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = width; ctx.stroke(); } };
  const text = (value: string, px: number, py: number, color: string, font = 20, align: CanvasTextAlign = 'center') => {
    ctx.font = '600 ' + font + 'px "PingFang SC", "Microsoft YaHei", sans-serif'; ctx.fillStyle = color; ctx.textAlign = align; ctx.textBaseline = 'middle'; ctx.fillText(value, px, py);
  };
  const star = (px: number, py: number, radius: number, color: string) => {
    ctx.beginPath();
    for (let index = 0; index < 10; index++) {
      const angle = index * Math.PI / 5 - Math.PI / 2; const r = index % 2 ? radius * 0.45 : radius;
      const nx = px + Math.cos(angle) * r, ny = py + Math.sin(angle) * r;
      if (index) ctx.lineTo(nx, ny); else ctx.moveTo(nx, ny);
    }
    ctx.closePath(); ctx.fillStyle = color; ctx.fill();
  };
  const chip = (value: string, px: number, py: number, color: string, font = 15) => {
    const width = value.length * font + 16;
    panel({ x: px - width / 2, y: py - font, w: width, h: font * 2 }, '#FFFFFFDD', color + '55', 1, font);
    text(value, px, py, color, font);
  };

  ctx.save();
  ctx.translate(x, y); ctx.scale(size / 1000, size / 1000);
  round({ x: 0, y: 0, w: 1000, h: 1000 }, 26); ctx.clip();
  const backdrop = ctx.createLinearGradient(0, 0, 0, 1000);
  backdrop.addColorStop(0, theme.sky); backdrop.addColorStop(1, theme.floor);
  ctx.fillStyle = backdrop; ctx.fillRect(0, 0, 1000, 1000);
  const glow = ctx.createRadialGradient(830, 130, 20, 830, 130, 430);
  glow.addColorStop(0, '#FFFFFF88'); glow.addColorStop(1, '#FFFFFF00');
  ctx.fillStyle = glow; ctx.fillRect(0, 0, 1000, 1000);

  // Work zones: one quiet outline per merged rectangle, with a single small tag each.
  for (const role of (activeRole === 'lead' ? ['follow', 'lead'] : ['lead', 'follow']) as RescueRole[]) {
    const isActive = activeRole === role;
    for (const rect of mergeZones(level.areas[role])) {
      ctx.globalAlpha = isActive ? 0.1 : 0.05; fill(rect, ROLE_SOFT[role], 24); ctx.globalAlpha = 1;
      round(rect, 24); ctx.setLineDash([15, 12]); ctx.lineWidth = isActive ? 2.4 : 1.6;
      ctx.strokeStyle = ROLE_COLORS[role] + (isActive ? '80' : '38'); ctx.stroke(); ctx.setLineDash([]);
      // Keep the two tags apart: the lead sits in its top-left corner, the follow in its top-right.
      if (rect.w > 150 && rect.h > 90) {
        const label = role === 'lead' ? '先手区' : '后手区';
        const px = role === 'lead' ? rect.x + 62 : rect.x + rect.w - 62;
        chip(label, px, rect.y + 26, ROLE_COLORS[role], 15);
      }
    }
  }

  for (const zone of level.winds ?? []) {
    const live = pulseActive(zone, frame.tick);
    ctx.globalAlpha = live ? 0.45 : 0.18; fill(zone, '#D8F1F1', 20);
    round(zone, 20); ctx.setLineDash([9, 10]); ctx.strokeStyle = '#7FB2BB'; ctx.lineWidth = 1.5; ctx.stroke(); ctx.setLineDash([]);
    const direction = zone.force.x < 0 ? -1 : 1;
    for (let row = 0; row < 3; row++) {
      const py = zone.y + zone.h * (0.26 + row * 0.24);
      const phase = ((time * 46 + row * 34) % (zone.w + 70)) - 35;
      const px = direction > 0 ? zone.x + phase : zone.x + zone.w - phase;
      const tip = px + 34 * direction;
      line([{ x: px, y: py }, { x: tip, y: py }], '#4E8FA0', 2.4);
      line([{ x: tip - 9 * direction, y: py - 6 }, { x: tip, y: py }, { x: tip - 9 * direction, y: py + 6 }], '#4E8FA0', 2.4);
    }
    ctx.globalAlpha = 1; text('上升气流', zone.x + zone.w / 2, zone.y + 20, '#4E8FA0', 15);
  }

  for (const zone of level.pulses ?? []) {
    const live = frame.pulses?.find(entry => entry.id === zone.id)?.active ?? pulseActive(zone, frame.tick);
    panel({ x: zone.x - 12, y: zone.y - 14, w: zone.w + 24, h: 12 }, '#77808F', undefined, 1, 4);
    panel({ x: zone.x - 12, y: zone.y + zone.h + 2, w: zone.w + 24, h: 12 }, '#77808F', undefined, 1, 4);
    if (live) {
      ctx.save(); ctx.shadowColor = '#E58BA4'; ctx.shadowBlur = 22;
      panel(zone, '#EFA7BC88', '#C9718C', 2, 3);
      for (let px = zone.x + 4; px < zone.x + zone.w; px += 9) line([{ x: px, y: zone.y + 4 }, { x: px, y: zone.y + zone.h - 4 }], '#F7C4D3', 2.4);
      ctx.restore();
    } else {
      panel(zone, '#BFD3E026', '#B4C6DA', 1.5, 3);
      line([{ x: zone.x + zone.w / 2, y: zone.y + 8 }, { x: zone.x + zone.w / 2, y: zone.y + zone.h - 8 }], '#C6D6E4', 1.5, [6, 8]);
    }
    circle(zone.x + zone.w / 2, zone.y - 8, 4.5, live ? '#F09BB2' : '#C8E6DA');
  }

  for (const rail of level.rails) for (let index = 1; index < rail.points.length; index++) {
    const a = rail.points[index - 1], b = rail.points[index];
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    if (length < 1) continue;
    ctx.save(); ctx.translate(a.x, a.y); ctx.rotate(Math.atan2(b.y - a.y, b.x - a.x));
    panel({ x: 0, y: -6.5, w: length, h: 16 }, theme.stone + '26', undefined, 0, 7);
    panel({ x: 0, y: -7.5, w: length, h: 15 }, theme.cap, theme.stone + '99', 1.6, 7);
    line([{ x: 6, y: -4.6 }, { x: Math.max(6.5, length - 6), y: -4.6 }], '#FFFFFF70', 2.6);
    circle(3.5, 0, 3.4, theme.stone + 'BB'); circle(length - 3.5, 0, 3.4, theme.stone + 'BB');
    ctx.restore();
  }

  for (const spec of level.motions ?? []) {
    const rect = frame.motions?.find(entry => entry.id === spec.id) ?? motionRect(spec, frame.tick);
    const from = { x: spec.x + spec.w / 2, y: spec.y + spec.h / 2 };
    const to = { x: from.x + spec.travel.x, y: from.y + spec.travel.y };
    ctx.globalAlpha = 0.45; line([from, to], theme.stone + '88', 3, [2, 12]);
    circle(from.x, from.y, 4, '#FFFFFFAA', theme.stone + '66', 1); circle(to.x, to.y, 4, '#FFFFFFAA', theme.stone + '66', 1);
    ctx.globalAlpha = 1;
    panel({ x: rect.x, y: rect.y + 7, w: rect.w, h: rect.h }, theme.stone + '2E', undefined, 0, 5);
    panel(rect, spec.kind === 'platform' ? theme.wood : '#B9C3C9', theme.stone + '99', 1.6, 5);
    if (spec.kind === 'platform') {
      line([{ x: rect.x + 5, y: rect.y + rect.h * 0.34 }, { x: rect.x + rect.w - 5, y: rect.y + rect.h * 0.34 }], '#FFFFFF70', 2);
      panel({ x: rect.x - 2, y: rect.y - 2, w: 8, h: rect.h + 4 }, '#8A6F4B', undefined, 0, 3);
      panel({ x: rect.x + rect.w - 6, y: rect.y - 2, w: 8, h: rect.h + 4 }, '#8A6F4B', undefined, 0, 3);
    } else {
      for (let row = 0.34; row < 1; row += 0.33) line([{ x: rect.x + 3, y: rect.y + rect.h * row }, { x: rect.x + rect.w - 3, y: rect.y + rect.h * row }], '#7F8A94AA', 2);
    }
  }

  for (const zone of level.checkpoints) {
    ctx.globalAlpha = frame.leadPassed ? 0.14 : 0.2; fill(zone, '#FFFFFF', 18); ctx.globalAlpha = 1;
    round(zone, 18); ctx.setLineDash([9, 9]); ctx.lineWidth = 1.8; ctx.strokeStyle = frame.leadPassed ? '#6FA98C' : '#7FA9BC'; ctx.stroke(); ctx.setLineDash([]);
    chip(frame.leadPassed ? '已交接' : '交接区', zone.x + zone.w / 2, zone.y + 24, frame.leadPassed ? '#4E8F6E' : '#5C8FA4', 16);
  }

  for (const zone of level.hazards) {
    panel({ x: zone.x, y: zone.y + zone.h - 16, w: zone.w, h: 16 }, '#66727F', undefined, 1, 5);
    const count = Math.max(2, Math.round(zone.w / 34)); const step = zone.w / count;
    for (let index = 0; index < count; index++) {
      const px = zone.x + index * step;
      path([{ x: px + 3, y: zone.y + zone.h - 12 }, { x: px + step / 2, y: zone.y + 4 }, { x: px + step - 3, y: zone.y + zone.h - 12 }]);
      ctx.closePath(); ctx.fillStyle = '#E8B4B4'; ctx.fill(); ctx.strokeStyle = '#A96A72'; ctx.lineWidth = 1.6; ctx.stroke();
    }
  }

  for (const goal of level.goals) {
    const reached = frame.balls.find(ball => ball.id === goal.ball)?.rescued;
    const body = { x: goal.x, y: goal.y + goal.h * 0.24, w: goal.w, h: goal.h * 0.76 };
    const wash = ctx.createLinearGradient(0, body.y, 0, body.y + body.h);
    wash.addColorStop(0, reached ? '#B7E3C4' : '#D8ECDB'); wash.addColorStop(1, reached ? '#7CC29A' : '#A9D2B4');
    round(body, 14); ctx.fillStyle = wash; ctx.fill(); ctx.strokeStyle = theme.accent + 'BB'; ctx.lineWidth = 2; ctx.stroke();
    path([{ x: goal.x + goal.w * 0.06, y: goal.y + goal.h * 0.3 }, { x: goal.x + goal.w / 2, y: goal.y + 2 }, { x: goal.x + goal.w * 0.94, y: goal.y + goal.h * 0.3 }]);
    ctx.closePath(); ctx.fillStyle = reached ? '#8CCBA3' : '#BEDFC8'; ctx.fill(); ctx.strokeStyle = theme.accent + 'BB'; ctx.lineWidth = 2; ctx.stroke();
    const cx = goal.x + goal.w / 2, cy = body.y + body.h * 0.5;
    circle(cx, cy, Math.min(20, goal.w * 0.2), '#FFFFFFAA');
    star(cx, cy, Math.min(13, goal.w * 0.13), reached ? '#D8A64B' : '#6FA98C');
    text(reached ? '到家' : '终点', cx, body.y + body.h - 14, '#4C7C63', 15);
    if (reached) for (let index = 0; index < 4; index++) star(cx + Math.cos(index * 1.7) * goal.w * 0.5, goal.y + 14 + Math.sin(index * 1.7) * 9, 4, '#DFB15E');
  }

  if (future) for (const role of ['lead', 'follow'] as const) for (const event of future[role].events) if (event.kind === 'add' && event.tick > frame.tick) {
    ctx.globalAlpha = 0.15; line(event.stroke.points, ROLE_COLORS[role], 5, [8, 10]); ctx.globalAlpha = 1;
  }
  for (const stroke of frame.strokes) {
    line(stroke.points, '#FFFFFFC0', 11);
    line(stroke.points, ROLE_COLORS[stroke.role], stroke.material === 'spring' ? 7.5 : 6.5, stroke.material === 'spring' ? [9, 7] : []);
    if (stroke.material === 'spring') line(stroke.points, '#FFFFFF80', 2);
  }
  if (preview) { ctx.globalAlpha = 0.7; line(preview.points, preview.color, 7, preview.spring ? [9, 7] : []); ctx.globalAlpha = 1; }

  for (const ball of frame.balls) {
    if (ball.y > 1060 || ball.y < -120 || ball.x < -120 || ball.x > 1120) continue;
    const radius = ball.radius + 3;
    ctx.save(); ctx.translate(ball.x, ball.y + 3); ctx.globalAlpha = 0.16; circle(0, 0, radius, '#33404A'); ctx.globalAlpha = 1; ctx.restore();
    ctx.save(); ctx.translate(ball.x, ball.y);
    circle(0, 0, radius, '#FFFFFFE8');
    const skin = ctx.createRadialGradient(-radius * 0.35, -radius * 0.45, 2, 0, 0, radius * 1.25);
    skin.addColorStop(0, '#FFF3DC');
    skin.addColorStop(0.45, ball.broken ? '#E7A2A6' : ball.color);
    skin.addColorStop(1, ball.helper ? '#5B6F86' : ball.broken ? '#C4757F' : shade(ball.color));
    ctx.beginPath(); ctx.ellipse(0, 0, radius, ball.fragile ? radius * 1.12 : radius, 0, 0, Math.PI * 2); ctx.fillStyle = skin; ctx.fill();
    ctx.strokeStyle = '#00000022'; ctx.lineWidth = 1.2; ctx.stroke();
    if (ball.helper) star(0, 0, radius * 0.6, '#FFFFFFCC');
    else {
      circle(-radius * 0.32, -radius * 0.18, 2.4, '#4A5A61'); circle(radius * 0.32, -radius * 0.18, 2.4, '#4A5A61');
      ctx.beginPath(); ctx.arc(0, radius * 0.1, radius * 0.3, 0.25, Math.PI - 0.25); ctx.strokeStyle = '#7A6252AA'; ctx.lineWidth = 1.6; ctx.stroke();
    }
    if (ball.broken) line([{ x: 1, y: -radius + 4 }, { x: -4, y: -1 }, { x: 4, y: 4 }, { x: 0, y: radius - 3 }], '#93485A', 2.4);
    if (ball.rescued) { circle(0, 0, radius + 5, '#00000000', '#6FA98C', 2); star(radius + 6, -radius - 3, 5.5, '#DFB15E'); }
    ctx.restore();
  }
  ctx.restore();
  ctx.save(); ctx.translate(x, y); ctx.scale(size / 1000, size / 1000);
  round({ x: 1, y: 1, w: 998, h: 998 }, 26); ctx.strokeStyle = '#9FB1B7AA'; ctx.lineWidth = 2.4; ctx.stroke();
  ctx.restore();
}
